/**
 * Thin fetch wrapper for talking to the portal API.
 *
 * - Always sends cookies (credentials: 'include').
 * - Throws ApiError on non-2xx responses with parsed { type, message, details }.
 * - Returns the unwrapped `data` field for { data, ... } responses.
 */

export const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4003";

export class ApiError extends Error {
  public readonly status: number;
  public readonly type: string | undefined;
  public readonly details: unknown;

  constructor(
    status: number,
    message: string,
    type?: string,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.type = type;
    this.details = details;
  }
}

interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Path under the API root, e.g. "/api/auth/me" */
  path: string;
}

// Shared fetch + parse + error handling. Returns the raw JSON payload (or
// undefined for 204) so callers can either unwrap `data` or keep the envelope.
async function rawFetch(opts: ApiOptions): Promise<unknown> {
  const { path, body, headers, ...rest } = opts;

  const init: RequestInit = {
    credentials: "include",
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...rest,
  };

  const res = await fetch(`${API_URL}${path}`, init);

  // 204 / empty body
  if (res.status === 204) {
    return undefined;
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const err = (
      payload as {
        error?: { type?: string; message?: string; details?: unknown };
      } | null
    )?.error;
    throw new ApiError(
      res.status,
      err?.message ?? `Request failed (${res.status})`,
      err?.type,
      err?.details,
    );
  }

  return payload;
}

async function request<T>(opts: ApiOptions): Promise<T> {
  const payload = await rawFetch(opts);

  // Convention: API returns { data, meta? }. Unwrap.
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

// Like `request` but keeps the full `{ data, meta }` envelope — for list
// endpoints whose UI needs `meta.total`.
export async function requestEnvelope<T>(opts: ApiOptions): Promise<T> {
  return (await rawFetch(opts)) as T;
}

export const api = {
  get: <T>(path: string, init?: Omit<ApiOptions, "path" | "body" | "method">) =>
    request<T>({ ...init, path, method: "GET" }),
  post: <T>(
    path: string,
    body?: unknown,
    init?: Omit<ApiOptions, "path" | "body" | "method">,
  ) => request<T>({ ...init, path, method: "POST", body }),
  patch: <T>(
    path: string,
    body?: unknown,
    init?: Omit<ApiOptions, "path" | "body" | "method">,
  ) => request<T>({ ...init, path, method: "PATCH", body }),
  put: <T>(
    path: string,
    body?: unknown,
    init?: Omit<ApiOptions, "path" | "body" | "method">,
  ) => request<T>({ ...init, path, method: "PUT", body }),
  delete: <T>(
    path: string,
    init?: Omit<ApiOptions, "path" | "body" | "method">,
  ) => request<T>({ ...init, path, method: "DELETE" }),
};
